import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardType, FileUp, Loader2, Radio, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FileDropzone } from '../components/FileDropzone';
import { BreathingDot } from '../components/ui/breathing';
import { InputModeSwitch } from '../components/ui/input-mode-switch';
import { api, type RoutedIngestResult } from '../lib/api';

/** Below this a submission is not worth a run — the same floor the API applies. */
const MIN_CHARS = 10;

/** Keeps the two input areas the same size, so switching between them never reflows the page. */
const AREA = 'h-[46vh] min-h-[320px]';

/** One queued submission, as this page follows it. */
interface Queued {
  runId: string | null;
  label: string;
  duplicate: boolean;
}

/**
 * Handing something to the knowledge base without saying where it goes.
 *
 * **There is deliberately no destination control.** There used to be one — a dropdown of every
 * project, defaulting to "let the agent decide" — and it was this page's main source of confusion:
 * a card headed *Destination* listing twenty projects reads as a decision you are required to make,
 * when the whole premise of the page is that you do not have to make it. Picking a project also
 * silently switched to an entirely different mode (pinned, no placement pass) with nothing on screen
 * saying so. Pinning is still available and still makes sense — but on the project itself, where the
 * choice has already been made by being there (`ProjectStarter`), rather than as an option that makes
 * the destination look unresolved here.
 *
 * **Files lead; pasting is the alternative.** The two were a segmented control, which gave them equal
 * weight and made the first act on the page a choice between them. They are not equally common —
 * documents, decks and exported notes are what people actually have to hand, and pasting is the
 * fallback for when the thing is in a window rather than a file. So the dropzone is the page and
 * pasting is one quiet line under it: the default costs no decision, and the alternative costs one
 * click.
 */
export default function AddToKb() {
  const [pasting, setPasting] = useState(false);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState<Queued[]>([]);

  const chars = content.trim().length;
  const canSubmit = pasting ? chars >= MIN_CHARS : files.length > 0;

  const track = (result: RoutedIngestResult, label: string) => {
    const run = result.runs[0];
    setQueued((prev) => [
      {
        runId: run?.id ?? null,
        label,
        // The one thing the response can say for certain at this point, and worth saying: re-sending
        // something already absorbed is a no-op rather than a failure.
        duplicate: result.reasoning.includes('already been received'),
      },
      ...prev,
    ]);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      if (pasting) {
        const trimmed = content.trim();
        const label = `${trimmed.slice(0, 60)}${trimmed.length > 60 ? '…' : ''}`;
        track(await api.kbIngest.content({ content }), label);
        setContent('');
      } else {
        // Sequentially, not concurrently: each one queues work onto a queue that is serial per
        // project anyway, so firing them together only makes the failure of one harder to attribute.
        for (const file of files) {
          track(await api.kbIngest.file(file), file.name);
        }
        setFiles([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-surface">

      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full overflow-y-auto">
        {/* Wider than the console's reading pages, because what is on it is the input rather than
            something to read: a pasted transcript at prose width is a column of wrapped lines nobody
            can scan, and a wide dropzone is a bigger target. Still capped — a textarea the full width
            of a 34" monitor is worse than one that is merely generous. */}
        <div className="max-w-5xl mx-auto px-8 py-8 space-y-5">
          {/* One line, and the only prose on the page: everything the old three sentences here and
              the paragraph beside the button explained ("you don't pick a destination") is implied
              by there being nothing to pick. text-lg matches every other page title. */}
          <div>
            <h1 className="text-lg font-bold text-ink">Drop context</h1>
            <p className="text-xs text-ink-muted mt-1">
              An agent decides which projects it belongs in and files it, citing the source.
            </p>
          </div>

          {pasting ? (
            <div className="space-y-2">
              {/* Sized in viewport height rather than rows: the useful area is "as much of this screen
                  as can be spared", which a row count cannot express across a laptop and a desktop. */}
              <textarea
                autoFocus
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste a meeting note, a thread, a decision…"
                className={`w-full ${AREA} rounded-xl border border-border bg-card px-4 py-3.5 text-sm leading-relaxed text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:border-orange-300 resize-y`}
              />
              <div className="flex items-center justify-between gap-3 text-[11px]">
                <InputModeSwitch
                  icon={FileUp}
                  label="Upload files instead"
                  onClick={() => setPasting(false)}
                />
                {chars > 0 && (
                  <span
                    className={
                      chars < MIN_CHARS ? 'text-amber-600 dark:text-amber-400' : 'text-ink-faint'
                    }
                  >
                    {chars.toLocaleString()} characters
                    {chars < MIN_CHARS && ` — needs at least ${MIN_CHARS}`}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <FileDropzone
                onFilesAdded={(added) => setFiles((prev) => [...prev, ...added])}
                disabled={busy}
                label="Drop files here, or browse"
                helperText="PDF, Word, Markdown or text · up to 25 MB each"
                zoneClassName={`${AREA} rounded-xl`}
              />
              {files.length > 0 && (
                <ul className="grid gap-2 sm:grid-cols-2 pt-1">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between gap-2 text-xs bg-card border border-border rounded-lg px-3 py-2.5"
                    >
                      <span className="min-w-0">
                        <span className="block text-ink truncate">{f.name}</span>
                        <span className="text-[10px] text-ink-faint">{(f.size / 1024).toFixed(0)} KB</span>
                      </span>
                      <button
                        onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-ink-faint hover:text-ink transition-colors flex-shrink-0"
                        aria-label={`Remove ${f.name}`}
                      >
                        <X size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {/* Below the dropzone *and* whatever it is holding: the primary act is dropping onto the
                  area above, so an alternative placed level with it competes for the same glance —
                  and placed between the two it would split a drop zone from the list of what was
                  dropped into it. */}
              <InputModeSwitch
                icon={ClipboardType}
                label="Paste text instead"
                onClick={() => setPasting(true)}
              />
            </div>
          )}

          {error && (
            <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded-lg px-3 py-2.5">
              {error}
            </p>
          )}

          {/* Just the button. The paragraph that sat beside it ("it reads every project… you don't
              pick a destination") restated the header's one-liner at three times the length; the
              pinning alternative it mentioned lives where the choice is made, on the project page. */}
          <div className="pt-1">
            <Button variant="primary" onClick={submit} disabled={!canSubmit || busy}>
              {busy ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 size={13} className="animate-spin" />
                  Handing it over…
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <Upload size={13} />
                  {!pasting && files.length > 1 ? `Drop ${files.length} files` : 'Drop it in'}
                </span>
              )}
            </Button>
          </div>

          {queued.length > 0 && (
            <div className="space-y-2 pt-4 border-t border-border-subtle">
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Handed over</h2>
              {/* Deliberately does **not** name a project.

                  It used to: the endpoint returns the run it created, which for unplaced content is
                  parked at the organization root, so this list rendered a green tick beside "CDR" —
                  the organization — for content on its way to Thrasos. That was a leftover from when
                  placement was a synchronous step in front of ingestion and the response really did
                  know the destinations. It is now the run's first phase, so at this moment nothing
                  knows them yet, and the honest thing is to point at the run where the answer appears
                  rather than to invent one here. */}
              {queued.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 bg-card border border-border rounded-lg px-3.5 py-3"
                >
                  <span className="min-w-0 flex items-center gap-2.5">
                    {item.duplicate ? (
                      <Radio size={13} className="text-ink-faint flex-shrink-0" />
                    ) : (
                      <BreathingDot size={8} />
                    )}
                    <span className="min-w-0">
                      <span className="block text-xs text-ink truncate">{item.label || 'Pasted text'}</span>
                      <span className="text-[11px] text-ink-muted">
                        {item.duplicate
                          ? 'Already absorbed earlier — nothing to do.'
                          : 'Deciding which projects this belongs in…'}
                      </span>
                    </span>
                  </span>
                  {item.runId && !item.duplicate && (
                    <Link
                      to={`/kb-review?tab=runs&run=${item.runId}`}
                      className="text-[11px] font-medium text-orange-600 dark:text-orange-400 hover:underline flex-shrink-0"
                    >
                      Watch it decide
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
